import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';

import { api } from '../lib/api.js';
import { useFiltros } from '../lib/filtros.jsx';
import { txt, TXT_PRI } from '../lib/enums.js';
import { useSesion } from '../piezas/Sesion.jsx';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Cargando, Error as AvisoError, Vacio } from '../piezas/Estados.jsx';

/**
 * Torre de control.
 *
 * Lo que se ve aqui NO es una tabla de alertas guardadas: es el estado de la
 * operacion calculado al mirarlo. Una desviacion cuya causa se resuelve
 * desaparece sola, sin que nadie tenga que acordarse de cerrarla.
 *
 * Por eso atender no borra: marca. La alerta sigue en la lista, con el nombre
 * de quien se dio por enterado y por cuanto tiempo. Esconderla seria inventar
 * que el problema se fue, y es justo el problema que una torre existe para
 * no tener.
 */

interface Alerta {
  regla: string;
  severidad: 'ALR_CRITICA' | 'ALR_ALTA' | 'ALR_MEDIA';
  entidad: string;
  entidad_id: string;
  etiqueta: string;
  titulo: string;
  vence_en: string | null;
  contexto: string | null;
  cliente: string | null;
  atendida_en: string | null;
  atencion_nota: string | null;
  atencion_hasta: string | null;
  atendida_por: string | null;
}

const SEVERIDAD: Record<Alerta['severidad'], { texto: string; clase: string }> = {
  ALR_CRITICA: { texto: 'Critica', clase: 'bad' },
  ALR_ALTA: { texto: 'Alta', clase: 'warn' },
  ALR_MEDIA: { texto: 'Media', clase: 'info' },
};

const REGLA: Record<string, string> = {
  ALR_SLA_SOLUCION: 'SLA de solucion',
  ALR_SLA_RESPUESTA: 'SLA de respuesta',
  ALR_SIN_ASIGNAR: 'Sin asignar',
  ALR_ETA_VENCIDA: 'Hora de llegada',
  ALR_CONTRATO_SALDO: 'Saldo de contrato',
  ALR_SITIO_REINCIDENTE: 'Sitio reincidente',
};

/** A donde lleva la alerta. Una torre que solo informa obliga a buscar. */
function destino(a: Alerta): string | null {
  if (a.entidad === 'ticket') return `/tickets?folio=${encodeURIComponent(a.etiqueta)}`;
  if (a.entidad === 'sitio') return `/sitios?busca=${encodeURIComponent(a.etiqueta)}`;
  if (a.entidad === 'contrato') return `/contratos?busca=${encodeURIComponent(a.etiqueta)}`;
  return null;
}

/**
 * Cuanto falta, en palabras.
 *
 * En minutos y horas y no en fecha completa: quien mira la torre decide a que
 * corre primero, y "en 40 min" se compara de un vistazo con "en 3 h". Una
 * marca de tiempo obliga a restar mentalmente, y se resta mal con prisa.
 */
function cuando(iso: string | null): string {
  if (iso === null) return '—';
  const minutos = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  const signo = minutos < 0 ? 'hace ' : 'en ';
  const m = Math.abs(minutos);
  if (m < 60) return `${signo}${m} min`;
  if (m < 48 * 60) return `${signo}${Math.round(m / 60)} h`;
  return `${signo}${Math.round(m / 1440)} d`;
}

export function Torre(): JSX.Element {
  const { consulta: filtrosQuery } = useFiltros();
  const { puede } = useSesion();
  const puedeAtender = puede('MOD_TORRE', 'escribir');

  const [alertas, setAlertas] = useState<Alerta[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [severidad, setSeveridad] = useState('');
  const [verAtendidas, setVerAtendidas] = useState(false);
  const [atendiendo, setAtendiendo] = useState<Alerta | null>(null);
  const [nota, setNota] = useState('');
  const [horas, setHoras] = useState(24);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(() => {
    const p = new URLSearchParams(filtrosQuery.replace(/^\?/, ''));
    if (severidad) p.set('severidad', severidad);
    if (verAtendidas) p.set('atendidas', '1');
    setAlertas(null);
    setError(null);
    api
      .obtener<Alerta[]>(`/v1/alertas?${p.toString()}`)
      .then(({ datos }) => setAlertas(datos))
      .catch(setError);
  }, [filtrosQuery, severidad, verAtendidas]);

  useEffect(cargar, [cargar]);

  const porSeveridad = useMemo(() => {
    const c = { ALR_CRITICA: 0, ALR_ALTA: 0, ALR_MEDIA: 0 };
    for (const a of alertas ?? []) c[a.severidad] += 1;
    return c;
  }, [alertas]);

  async function atender(): Promise<void> {
    if (!atendiendo) return;
    setGuardando(true);
    try {
      await api.enviar('/v1/alertas/atender', {
        regla: atendiendo.regla,
        entidad_id: atendiendo.entidad_id,
        ...(nota.trim().length >= 3 ? { nota: nota.trim() } : {}),
        horas,
      });
      setAtendiendo(null);
      setNota('');
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  if (error !== null)
    return (
      <>
        <Pagehead />
        <AvisoError error={error} />
      </>
    );

  return (
    <>
      <Pagehead />

      <div className="card">
        <div className="toolbar">
          <span className="chip-dato bad" data-prueba="torre-criticas">
            {porSeveridad.ALR_CRITICA} criticas
          </span>
          <span className="chip-dato warn">{porSeveridad.ALR_ALTA} altas</span>
          <span className="chip-dato info">{porSeveridad.ALR_MEDIA} medias</span>

          <label className="sel">
            <span>Severidad</span>
            <select
              value={severidad}
              onChange={(e) => setSeveridad(e.target.value)}
              data-prueba="torre-severidad"
            >
              <option value="">Todas</option>
              <option value="ALR_CRITICA">Criticas</option>
              <option value="ALR_ALTA">Altas</option>
              <option value="ALR_MEDIA">Medias</option>
            </select>
          </label>

          <label className="sel">
            <input
              type="checkbox"
              checked={verAtendidas}
              onChange={(e) => setVerAtendidas(e.target.checked)}
              data-prueba="torre-atendidas"
            />
            <span>Incluir atendidas</span>
          </label>

          <span className="sp" />
          <span className="mini">
            Se calcula al mirarla: una desviacion resuelta desaparece sola
          </span>
        </div>

        {alertas === null ? (
          <Cargando />
        ) : alertas.length === 0 ? (
          /* El vacio BUENO de esta pantalla. Decirlo asi importa: una torre
             en blanco se parece demasiado a una torre rota. */
          <Vacio
            mensaje={
              verAtendidas || severidad
                ? 'Nada coincide con lo que estas mirando.'
                : 'Sin desviaciones pendientes. La operacion va dentro de lo comprometido.'
            }
            icono="🛫"
          />
        ) : (
          <div className="tabla-mapa">
            <table data-prueba="tabla-torre">
              <thead>
                <tr>
                  <th scope="col">Severidad</th>
                  <th scope="col">Que pasa</th>
                  <th scope="col">Referencia</th>
                  <th scope="col">Cliente</th>
                  <th scope="col">Vence</th>
                  <th scope="col">Detalle</th>
                  <th scope="col">Atencion</th>
                </tr>
              </thead>
              <tbody>
                {alertas.map((a) => {
                  const sev = SEVERIDAD[a.severidad];
                  const url = destino(a);
                  return (
                    <tr key={`${a.regla}-${a.entidad_id}`} data-prueba="fila-alerta">
                      <td>
                        {/* El grado va escrito, no solo con color: quien no
                            distingue los tonos lee "Critica" igual. */}
                        <span className={`chip-dato ${sev.clase}`}>{sev.texto}</span>
                      </td>
                      <td>
                        <b>{a.titulo}</b>
                        <div className="mini">{REGLA[a.regla] ?? a.regla}</div>
                      </td>
                      <td>
                        {url ? (
                          <a className="folio" href={url}>
                            {a.etiqueta}
                          </a>
                        ) : (
                          <span className="folio">{a.etiqueta}</span>
                        )}
                      </td>
                      <td>{a.cliente ?? '—'}</td>
                      <td className="num">{cuando(a.vence_en)}</td>
                      <td className="mini">
                        {/* El contexto viene de dos clases de regla: las de
                            ticket mandan la prioridad —un enum, que hay que
                            traducir antes de ensenarlo— y las de contrato y
                            sitio mandan una frase ya hecha. Sin esto la
                            pantalla imprimia PRI_CRITICA tal cual, que es
                            justo lo que la suite prohibe en cualquier celda. */}
                        {a.contexto === null
                          ? '—'
                          : a.contexto.startsWith('PRI_')
                            ? txt(TXT_PRI, a.contexto)
                            : a.contexto}
                      </td>
                      <td>
                        {a.atendida_en ? (
                          <>
                            <span className="chip-dato ok">Atendida</span>
                            <div className="mini">
                              {a.atendida_por}
                              {a.atencion_nota ? ` · ${a.atencion_nota}` : ''}
                            </div>
                          </>
                        ) : puedeAtender ? (
                          <button
                            type="button"
                            className="btn sm"
                            onClick={() => {
                              setAtendiendo(a);
                              setNota('');
                            }}
                            data-prueba="atender"
                          >
                            Atender
                          </button>
                        ) : (
                          <span className="mini">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {atendiendo && (
        <div className="card" style={{ marginTop: '14px' }} data-prueba="panel-atender">
          <h2 style={{ marginTop: 0 }}>Atender · {atendiendo.etiqueta}</h2>
          <p className="mini" style={{ marginTop: 0 }}>
            {/* Se dice lo que el boton hace Y lo que NO hace. Un control que
                parece cerrar la alerta y solo la silencia es una trampa. */}
            Esto no resuelve la desviacion ni cierra nada: registra que la viste y la guarda de la
            lista de pendientes por el tiempo que elijas. Si al terminar sigue ahi, vuelve a
            aparecer.
          </p>

          <div className="campo">
            <label htmlFor="torre-nota">Nota (opcional)</label>
            <input
              id="torre-nota"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Que se hizo o que se espera"
              maxLength={500}
              data-prueba="torre-nota"
            />
          </div>

          <div className="campo">
            <label htmlFor="torre-horas">Guardar de pendientes por</label>
            <select
              id="torre-horas"
              value={horas}
              onChange={(e) => setHoras(Number(e.target.value))}
              data-prueba="torre-horas"
            >
              <option value={4}>4 horas</option>
              <option value={24}>1 dia</option>
              <option value={72}>3 dias</option>
              <option value={168}>1 semana</option>
            </select>
          </div>

          <div className="fila">
            <button
              type="button"
              className="btn pri"
              onClick={() => void atender()}
              disabled={guardando}
              data-prueba="confirmar-atender"
            >
              {guardando ? 'Guardando...' : 'Registrar atencion'}
            </button>
            <button type="button" className="btn ghost" onClick={() => setAtendiendo(null)}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </>
  );
}
